import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Box } from '#src/atoms/icons/Box.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BrandXXS } from '#src/atoms/typography/index.tsx';
import { Variant } from '#src/components/rewards/AnyRewardConfig/index.tsx';

import type { RewardMultiplier_lineup } from './__generated__/index.graphql.ts';

const DEFAULT_COLOR = '173, 173, 173';

type Props = {
  lineup: RewardMultiplier_lineup;
  variant?: Variant;
};

const ImageContainer = styled(Horizontal).attrs({ center: true })`
  aspect-ratio: 1/1;
  margin: auto;
  &:not(.iconOnly &) {
    height: 70%;
  }
`;

const Chip = styled(Horizontal).attrs({ gap: 1 })`
  width: max-content;
  height: var(--quadruple-unit);
  gap: var(--half-unit);
  padding: 0 var(--unit);

  border-radius: var(--unit);
  border: 0.5px solid transparent;
  background:
    radial-gradient(
        circle var(--double-unit) at var(--double-unit) 50%,
        rgba(var(--color), 0.3),
        rgba(var(--color), 0)
      )
      padding-box,
    linear-gradient(#262626, #262626) padding-box,
    linear-gradient(rgb(var(--color)), rgba(var(--color), 0.2)) border-box;
  background-repeat: no-repeat;
`;

export const RewardMultiplier = ({ lineup, variant = Variant.CHIP }: Props) => {
  const boost = lineup.rewardMultiplier - 1;
  if (boost <= 0) {
    return null;
  }

  if (variant === Variant.TEXT) {
    return (
      <Horizontal gap={0.5}>
        <Box gradient="green" size={1.5} />
        <BrandXXS
          as="p"
          bold
          className="text-center text-gradient-color"
          style={
            {
              '--text-gradient-color': 'var(--c-gradient-greenYellow)',
            } as CSSProperties
          }
        >
          <FormattedMessage
            id="RewardMultiplier.multiplier"
            defaultMessage="{bonusPct, number, ::sign-always .## percent} boost"
            values={{
              bonusPct: lineup.rewardMultiplier - 1,
            }}
          />
        </BrandXXS>
      </Horizontal>
    );
  }

  return (
    <Chip style={{ '--color': DEFAULT_COLOR } as CSSProperties}>
      <ImageContainer>
        <Box gradient="green" />
      </ImageContainer>
      <BrandXXS
        as="p"
        bold
        className="text-center text-gradient-color"
        style={
          {
            '--text-gradient-color': 'var(--c-gradient-greenYellow)',
          } as CSSProperties
        }
      >
        <FormattedNumber
          value={lineup.rewardMultiplier - 1}
          style="percent"
          maximumFractionDigits={2}
          signDisplay="always"
        />
      </BrandXXS>
    </Chip>
  );
};

RewardMultiplier.fragments = {
  lineup: gql`
    fragment RewardMultiplier_lineup on So5Lineup {
      id
      rewardMultiplier
    }
  ` as TypedDocumentNode<RewardMultiplier_lineup>,
};
